// Shopify Buy Button helpers for <shopify-carousel>.
//
// The SDK is loaded once per page and every product component is created from one `ui` per
// shop, so all of them share a single cart. The script is tagged data-categories="essential"
// like the site's own scripts, so the consent manager does not hold it back.

export const SDK_URL = 'https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js'

// Same look as the Featured Products embed that this component replaces. A card is as wide as its
// slide, so the width rules of the collection grid are not carried over.
export const DEFAULT_OPTIONS = {
  product: {
    styles: {
      product: {
        '@media (min-width: 601px)': {
          'max-width': '100%',
          'margin-left': '0',
          'margin-bottom': '0'
        }
      },
      button: {
        ':hover': { 'background-color': '#c4056b' },
        'background-color': '#e8057f',
        ':focus': { 'background-color': '#c4056b' },
        'border-radius': '10px'
      },
      // Every card is its own iframe, a few hundred pixels wide, so the embed's 631 px media query
      // never matched inside it. A fixed title height keeps prices and buttons on one line across cards.
      title: { height: '60px', overflow: 'hidden' },
      price: { color: '#b21f4f' },
      compareAt: { color: '#b21f4f' },
      unitPrice: { color: '#b21f4f' }
    },
    text: { button: 'Add to cart' }
  },
  modalProduct: {
    contents: {
      img: false,
      imgWithCarousel: true,
      button: false,
      buttonWithQuantity: true
    },
    styles: {
      product: {
        '@media (min-width: 601px)': {
          'max-width': '100%',
          'margin-left': '0px',
          'margin-bottom': '0px'
        }
      },
      button: {
        ':hover': { 'background-color': '#4a949e' },
        'background-color': '#52a4af',
        ':focus': { 'background-color': '#4a949e' },
        'border-radius': '10px'
      },
      title: { color: '#24366a' },
      price: {
        'font-family': 'Helvetica Neue, sans-serif',
        'font-weight': 'normal',
        'font-size': '18px',
        color: '#b21f4f'
      },
      compareAt: {
        'font-family': 'Helvetica Neue, sans-serif',
        'font-weight': 'normal',
        'font-size': '15.299999999999999px',
        color: '#b21f4f'
      },
      unitPrice: {
        'font-family': 'Helvetica Neue, sans-serif',
        'font-weight': 'normal',
        'font-size': '15.299999999999999px',
        color: '#b21f4f'
      }
    },
    text: { button: 'Add to cart' }
  },
  option: {},
  cart: {
    styles: {
      button: {
        ':hover': { 'background-color': '#4a949e' },
        'background-color': '#52a4af',
        ':focus': { 'background-color': '#4a949e' },
        'border-radius': '10px'
      }
    },
    text: { total: 'Subtotal', button: 'Checkout' }
  },
  toggle: {
    styles: {
      toggle: {
        'background-color': '#52a4af',
        ':hover': { 'background-color': '#4a949e' },
        ':focus': { 'background-color': '#4a949e' }
      }
    }
  }
}

// "gid://shopify/Product/7045" and "7045" both become "7045". The product component renders an
// empty frame when it is given the global id.
export function numericId (id) {
  return String(id).split('/').pop()
}

// The Storefront API wants the collection as a base64 global id; the embed only has the number.
export function collectionGid (id) {
  const value = String(id)
  if (!/^\d+$/.test(value)) return value
  return window.btoa('gid://shopify/Collection/' + value)
}

let sdkPromise = null

export function loadSdk () {
  if (window.ShopifyBuy && window.ShopifyBuy.UI) return Promise.resolve(window.ShopifyBuy)
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.async = true
      script.src = SDK_URL
      script.setAttribute('data-categories', 'essential')
      script.onload = () => resolve(window.ShopifyBuy)
      script.onerror = () => {
        sdkPromise = null
        script.remove()
        reject(new Error('The Shopify SDK did not load'))
      }
      document.head.appendChild(script)
    })
  }
  return sdkPromise
}

const clients = {}

// One client and one `ui` per shop and token, kept in memory only. Remounting a block (the Page
// Builder preview does it) reuses them instead of creating a second cart.
export function getShop (domain, token) {
  const key = domain + '|' + token
  if (!clients[key]) {
    clients[key] = loadSdk()
      .then(ShopifyBuy => {
        const client = ShopifyBuy.buildClient({ domain: domain, storefrontAccessToken: token })
        return ShopifyBuy.UI.onReady(client).then(ui => ({ client: client, ui: ui }))
      })
      .catch(error => {
        delete clients[key]
        throw error
      })
  }
  return clients[key]
}

export function resetShops () {
  Object.keys(clients).forEach(key => delete clients[key])
  sdkPromise = null
}
